import React from 'react';
import styles from './ScoreDisplay.module.scss';
import ScoreSvg from './ScoreSvg';
import useApiData from '../../../../hooks/useApiData';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { getAbsoluteTimestamp } from '../../../../util/formatDate';
import { v4 as uuid } from 'uuid';
import APIClient from '../../../../util/APIClient';

function getPath({ scoreType, subjectType, userId, teamId }) {
  if (!['performance', 'processAdoption'].includes(scoreType)) {
    throw new Error('Invalid score type!');
  }

  switch (subjectType) {
    case 'user':
      if (!userId) {
        throw new Error('User ID needs to be provided!');
      }
      return `/score/users/${userId}/${scoreType}`;
    case 'teamMember':
      if (!teamId || !userId) {
        throw new Error('Both Team ID and User ID need to be provided!');
      }
      return `/score/teams/${teamId}/teamMembers/${userId}/${scoreType}`;
    case 'team':
      if (!teamId) {
        throw new Error('Team ID needs to be provided!');
      }
      return `/score/teams/${teamId}/${scoreType}`;
    case 'organisation':
      return `/score/${scoreType}`;
    default:
      throw new Error(
        'Invalid subject type! Subject type must be one of ["user", "team", "teamMember", "organisation"].',
      );
  }
}

function getParams({ subjectType, teamIds, fromDate, toDate }) {
  const params = {};
  if (fromDate) {
    params.fromDate = new Date(fromDate).toISOString().substring(0, 10);
  }
  if (toDate) {
    params.toDate = new Date(toDate).toISOString().substring(0, 10);
  }
  if (subjectType === 'organisation' && Array.isArray(teamIds)) {
    params.teamIds = teamIds.join(',');
  }
  return params;
}

function getTooltipTitle({
  scoreType,
  subjectType,
  teamIds,
  fromDate,
  toDate,
  score,
  components,
}) {
  let header = '';
  switch (scoreType) {
    case 'performance':
      header += 'Performance';
      break;
    case 'processAdoption':
      header += 'Process adoption';
      break;
  }
  header += ` Score - `;
  let body = 'Measured ';
  if (fromDate && toDate) {
    body += `between ${getAbsoluteTimestamp(new Date(fromDate))} and ${getAbsoluteTimestamp(new Date(toDate))}`;
  } else if (!fromDate && toDate) {
    body += `since creation until ${getAbsoluteTimestamp(new Date(toDate))}`;
  } else if (fromDate && !toDate) {
    body += `since ${getAbsoluteTimestamp(new Date(fromDate))} until now`;
  } else {
    body += 'since creation until now';
  }
  if (subjectType === 'organisation' && Array.isArray(teamIds)) {
    body += ' (for selected teams)';
  }
  body += '.';
  const componentsIncludedInScore = Array.isArray(components)
    ? components.filter((c) => c.includedInScore)
    : [];
  const additionalComponents = Array.isArray(components)
    ? components.filter((c) => !c.includedInScore)
    : [];
  return (
    <p style={{ fontSize: '1rem' }}>
      {header}
      <span style={{ fontWeight: 600 }}>
        {typeof score === 'number' ? (score * 100).toFixed() : 'n/a'}{' '}
      </span>
      {typeof score === 'number' && 'out of 100'}
      <br />
      <span
        style={{ fontStyle: 'italic', marginTop: '.5rem', display: 'block' }}
      >
        {body}
      </span>
      {componentsIncludedInScore.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              display: 'block',
              marginTop: '1rem',
              paddingBottom: '0.25rem',
              borderBottom: '1px solid white',
            }}
          >
            Score components
          </span>
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {componentsIncludedInScore.map((c) => {
              let id = uuid();
              return (
                <span
                  key={id}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItem: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                    marginTop: '0.5rem',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      wordBreak: 'keep-all',
                    }}
                  >
                    {c.label}
                  </span>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      marginLeft: '1rem',
                      wordBreak: 'keep-all',
                      minWidth: '25%',
                    }}
                  >
                    {c.valueTotal !== null
                      ? `${typeof c.value === 'number' ? c.value.toFixed(2) : '-'} / ${c.valueTotal}`
                      : `${typeof c.value === 'number' ? c.value.toFixed(2) : '-'}`}
                  </span>
                </span>
              );
            })}
          </span>
        </>
      )}
      {additionalComponents.length > 0 && (
        <>
          <span
            style={{
              fontWeight: 600,
              display: 'block',
              marginTop: '1rem',
              justifyContent: 'space-between',
              paddingBottom: '0.25rem',
              borderBottom: '1px solid white',
            }}
          >
            Additional information
          </span>
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {additionalComponents.map((c) => {
              let id = uuid();
              return (
                <span
                  key={id}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItem: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                    marginTop: '0.5rem',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      wordBreak: 'keep-all',
                    }}
                  >
                    {c.label}
                  </span>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      marginLeft: '1rem',
                      wordBreak: 'keep-all',
                      minWidth: '25%',
                    }}
                  >
                    {c.valueTotal !== null
                      ? `${typeof c.value === 'number' ? c.value.toFixed(2) : '-'} / ${c.valueTotal}`
                      : `${typeof c.value === 'number' ? c.value.toFixed(2) : '-'}`}
                  </span>
                </span>
              );
            })}
          </span>
        </>
      )}
    </p>
  );
}

export default function ScoreDisplay(props) {
  const { grow } = props;
  const path = getPath(props);
  const params = getParams(props);
  const { data, error, isLoading, reload } = useApiData({
    path,
    params,
  });
  const tooltipTitle = isLoading
    ? 'Loading score...'
    : error
      ? 'Could not load score!'
      : getTooltipTitle({
          ...props,
          score: data.score,
          components: data.components,
        });

  React.useEffect(() => {
    reload(true);
  }, [props.teamIds]);

  return (
    <Tooltip title={tooltipTitle}>
      <div className={`${styles.scoreDisplay} ${grow ? styles.grow : ''}`}>
        {(isLoading && <CircularProgress />) || (error && <p>ERR</p>) || (
          <ScoreSvg score={data.score} />
        )}
      </div>
    </Tooltip>
  );
}
